// src/components/accounts/AccountsForm.tsx
import React, { useEffect, useState } from "react";
import { CircularProgress, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Swal from "sweetalert2";

import DynamicForm from "../../../../data/Axios/DynamicForm";
import { generateAndExecuteMutation, graphqlQuery } from "../../../../data/Axios/DynamicService";
import { AccountsandFinanceurl } from "../../../../config";
import { allAccountSubCategories } from "../../../../data/Axios/queries";

const base_url = AccountsandFinanceurl.uri;

/* --------------------------------------------------------------
   1. Field definition – **inside the file**, just like BankBranchForm
   -------------------------------------------------------------- */

/* --------------------------------------------------------------
   2. The form component
   -------------------------------------------------------------- */
const AccountsForm = (props) => {
  // ---------- SESSION ----------
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id, 10) || null;
  const companyId = parseInt(decodedToken.CompanyId, 10) || null;
  const companyName = decodedToken.CompanyName || "";

  // ---------- STATE ----------
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);

const isEditing = props.isEditing || false;
  const getAccountFields = (categoryData) => [
  {
    name: "accountName",
    label: "Account Name",
    type: "text",
    required: true,
    component: TextField,
     gridColumn: "span 4",
  },
  ...(!isEditing
    ? [
        {
          name: "accountSubcategoryId",
          label: "Account Subcategory",
          type: "select",
          required: true,
          component: Autocomplete,
          options: (categoryData || [])
            .filter((c) => c.accountCategoryId === 2)
            .map((c) => ({ label: c.accountName, value: c.id })),
          getOptionLabel: (opt) => opt.label,
          isOptionEqualToValue: (opt, val) => opt.value === val,
          gridColumn: "span 4",
        },
      ]
    : []),
];

  // ---------- FETCH SUBCATEGORIES ----------
  useEffect(() => {
    async function fetchAllData() {
      try {
        const data = await graphqlQuery(allAccountSubCategories, base_url);
        setCategoryData(data || []);
      } catch (err) {
        console.error("Error fetching sub-categories:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchAllData();
  }, []);

  // ---------- INITIAL VALUES ----------
  const initialValues = {
    accountName: props.data?.displayName ?? "",
    accountSubcategoryId: props.data?.accountSubcategoryId ?? null,
  };

  // ---------- ACCOUNT NUMBER GENERATOR ----------
  const generateAccountNumber = () =>
    `${(Math.random() * 1000000).toFixed(0).padStart(5, "0")}-${new Date()
      .getFullYear()
      .toString()
      .slice(-2)}`;

  // ---------- SUBMIT ----------
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { accountName, accountSubcategoryId } = values;

      // 1. generate number on the client
      const accountNumber = generateAccountNumber();

      // 2. payload exactly as the backend expects
      const basePayload = {
        status: "Active",
        accountName,
        accountSubcategoryId,
        accountNumber,
        companyId,
        company: companyName,
        isActive: 1,
        identifierType: 1,               // hard-coded
        identifierId: companyId,         // from session
        createdBy: userId,
        // createdDate filled by DB ([DateNow])
      };

      const mutationData = props.isEditing
        ? {
            id: props.data.id,
            ...basePayload,
            updatedBy: userId,
          }
        : basePayload;

      const response = await generateAndExecuteMutation(
        props.isEditing ? "updateAccounts" : "createAccounts",
        mutationData,
        props.isEditing ? "accounts" : "accounts",
        base_url
      );

      if (Object.values(response)[0] != null) {
        props.onClose();
        props.onAction();

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Account ${props.isEditing ? "updated" : "created"} successfully.`,
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- RENDER ----------
  if (loading) return <CircularProgress />;

  // Build fields **once** with the fetched data
  const fields = getAccountFields(categoryData);

  return (
    <DynamicForm
      initialValues={initialValues}
      fields={fields}
      onSubmit={handleSubmit}
      title={props.isEditing ? "Edit Account" : "Create Account"}
      subtitle={
        props.isEditing ? "Edit an Existing Account" : "Create a New Account"
      }
    />
  );
};

export default AccountsForm;